/**
 * CollapsibleAdminSection — Thin admin-page wrapper that starts collapsed
 * and lets the admin expand a heavy / rarely-needed panel on demand.
 *
 * Used to declutter the admin home when we have legacy / low-signal
 * panels (free score submissions, consent logs, inspirations…) that we
 * still want available but not competing for attention.
 *
 * Renders the children only AFTER the first open so the underlying
 * panel's data-fetching / effects don't run on every admin page load.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';

export function CollapsibleAdminSection({
  title,
  description = null,
  icon = null,
  testId,
  defaultOpen = false,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [hasEverOpened, setHasEverOpened] = useState(defaultOpen);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) setHasEverOpened(true);
  };
  return (
    <Card className="border-border" data-testid={testId}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
        className="w-full text-left"
      >
        <div className="p-4 border-b border-border flex items-center gap-2 hover:bg-muted/40 transition-colors">
          {icon}
          <div className="min-w-0">
            <h2 className="font-semibold leading-tight">{title}</h2>
            {description && (
              <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">{description}</p>
            )}
          </div>
          <span className="ml-auto text-muted-foreground">
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </div>
      </button>
      {hasEverOpened && (
        <div hidden={!open}>
          <CardContent className="p-0">{children}</CardContent>
        </div>
      )}
    </Card>
  );
}

export default CollapsibleAdminSection;
